// PublicSignUp.js
import React, { useState, useEffect, useRef } from 'react';
import './PublicSignUp.css';
import { useParams, Navigate } from 'react-router-dom';

export default function PublicSignUp() {
  // Photo/file state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(''); // ✅ pre-compressed base64 for fast upload
  const [photoPrepError, setPhotoPrepError] = useState('');

  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Overlay state for spinner + messages
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayMessage, setOverlayMessage] = useState('');
  const overlayTimerRef = useRef(null);

  const [overlayStatus, setOverlayStatus] = useState('loading');

  // Parsed store info
  const [storeInfoParsed, setStoreInfoParsed] = useState({
    storeName: '',
    storeNumber: '',
    accessMode: 'STANDARD',
    isInstantAccess: false,
  });

  const formatStoreName = (name) =>
    name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/-/g, ' ').trim();

  const [isValid, setIsValid] = useState(true);
  const { storeInfo } = useParams();

  // ===== MEMBERSHIP PRICING SOURCE (MASTER STORE LIST) CHANGE START =====
  // Plan loading state + data
  const [membershipPlans, setMembershipPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState('');

  // Store config state (source of truth)
  // Expected values: "Talvik" or "Clover"
  const [membershipPricingSource, setMembershipPricingSource] = useState('');
  const [storeConfigLoading, setStoreConfigLoading] = useState(false);
  const [storeConfigError, setStoreConfigError] = useState('');

 
  const isPlanMode = String(membershipPricingSource || '').toLowerCase() === 'talvik';

  const [allowKeyFobs, setAllowKeyFobs] = useState(true);

  const parseBool = (val, defaultValue = true) => {
    if (val === true || val === false) return val;
    if (val == null) return defaultValue;

    const s = String(val).trim().toLowerCase();
    if (['true', 'yes'].includes(s)) return true;
    if (['false', 'no'].includes(s)) return false;
    return defaultValue;
  };

  const formatDurationLabel = (months) => {
    const m = Number(months || 0);
    if (!Number.isFinite(m) || m <= 0) return '';
    if (m === 12) return '1 Year';
    if (m % 12 === 0) return `${m / 12} Year`;
    return `${m} Month`;
  };

  const [formData, setFormData] = useState({
    storeNumber: '',
    storeName: '',
    accessMode: 'STANDARD',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    accessType: 'Phone App',
    keyFobCode: '',
    membershipDuration: '',
    membershipLevel: '',
    photo: null, 
    honeypot: '',
    addOn1Name: '',
    addOn1Email: '',
    addOn1Phone: '',
    addOn1Access: 'Phone App',
    addOn1FobCode: '',
    addOn2Name: '',
    addOn2Email: '',
    addOn2Phone: '',
    addOn2Access: 'Phone App',
    addOn2FobCode: '',

    // Plan-based fields (Talvik pricing source)
    planId: '',
    planName: '',
    durationMonths: '',
    signUpPrice: '',
    renewalPrice: '',
    smsConsent: false,
    smsConsentTimestamp: '',
  });

  const INITIAL_FORM_STATE = {
    storeNumber: '',
    storeName: '',
    accessMode: 'STANDARD',
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    accessType: 'Phone App',
    keyFobCode: '',
    membershipDuration: '',
    membershipLevel: '',
    photo: null, 
    honeypot: '',
    addOn1Name: '',
    addOn1Email: '',
    addOn1Phone: '',
    addOn1Access: 'Phone App',
    addOn1FobCode: '',
    addOn2Name: '',
    addOn2Email: '',
    addOn2Phone: '',
    addOn2Access: 'Phone App',
    addOn2FobCode: '',

    // Plan-based fields (Talvik pricing source)
    planId: '',
    planName: '',
    durationMonths: '',
    signUpPrice: '',
    renewalPrice: '',
    smsConsent: false,
    smsConsentTimestamp: '',
  };

  // Needed to clear the <input type="file" ...> value reliably
  const photoInputRef = useRef(null);

  const resetForm = () => {
    // clear local file/base64 state
    setPhotoFile(null);
    setPhotoBase64('');
    setPhotoPrepError('');

    // close add-ons modal if open
    setShowAddOnsModal(false);

    // reset all fields, but keep store context
    setFormData(() => {
      const base = {
        ...INITIAL_FORM_STATE,
        storeName: storeInfoParsed.storeName,
        storeNumber: storeInfoParsed.storeNumber,
        accessMode: storeInfoParsed.accessMode,
      
        accessType: allowKeyFobs ? '' : 'Phone App',
        keyFobCode: '',
        addOn1Access: 'Phone App',
        addOn1FobCode: '',
        addOn2Access: 'Phone App',
        addOn2FobCode: '',
      };

      // If Talvik plan mode, reselect default plan automatically
      if (isPlanMode && membershipPlans.length) {
        const defaultPlan = membershipPlans.find((p) => p.isDefault) || membershipPlans[0];
        if (defaultPlan) {
          const durationLabel = formatDurationLabel(defaultPlan.durationMonths);
          return {
            ...base,
            planId: defaultPlan.planId,
            planName: defaultPlan.planName,
            durationMonths: String(defaultPlan.durationMonths),
            signUpPrice: String(defaultPlan.signUpPrice),
            renewalPrice: defaultPlan.renewalPrice === '' ? '' : String(defaultPlan.renewalPrice),
            // compatibility (legacy field)
            membershipDuration: durationLabel,
          };
        }
      }

      return base;
    });

    // clear the actual file input element value
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
    try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {
          window.scrollTo(0, 0);
        }
  };

  // Let React paint before redirect (mobile friendly)
  const nextPaint = () =>
    new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  useEffect(() => {
    // parse storeInfo
    console.log('📦 Raw storeInfo from URL:', storeInfo);

    if (!storeInfo || !storeInfo.includes('.')) {
      console.warn('⚠️ Invalid store URL format. Use /StoreName.10 or /StoreName.10.IA');
      setIsValid(false);
      return;
    }
    const parts = storeInfo.split('.');
    const storeNameRaw = parts[0];
    const storeNumberRaw = parts[1];
    const modeRaw = (parts[2] || '').toUpperCase();

    const storeName = decodeURIComponent(storeNameRaw || '');
    const storeNumber = (storeNumberRaw || '').trim();
    const isValidStoreNumber = /^\d+$/.test(storeNumber);

    if (!storeName || !isValidStoreNumber) {
      console.warn('⚠️ Invalid format — storeName missing or storeNumber not numeric');
      setIsValid(false);
      return;
    }

    const isInstantAccess = modeRaw === 'IA';
    const accessMode = isInstantAccess ? 'IA' : 'STANDARD';

    setStoreInfoParsed({ storeName, storeNumber, accessMode, isInstantAccess });

    setFormData((prev) => ({
      ...prev,
      storeName,
      storeNumber,
      accessMode,
    }));

    console.log('✅ Parsed store info:', { storeName, storeNumber, accessMode });
  }, [storeInfo]);

  // Clear any timers on unmount
  useEffect(() => {
    return () => {
      if (overlayTimerRef.current) {
        clearTimeout(overlayTimerRef.current);
        overlayTimerRef.current = null;
      }
    };
  }, []);

  
  // Targets mobile viewing: ~150–400 KB
  const resizeImageToBase64 = (file, maxWidth = 1200, quality = 0.7) => {
    return new Promise((resolve, reject) => {
      if (!file) return resolve('');
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('FileReader error'));
      reader.onload = (ev) => {
        const img = new Image();
        img.onload = () => {
          try {
            const ratio = img.width > maxWidth ? maxWidth / img.width : 1;
            const w = Math.round(img.width * ratio);
            const h = Math.round(img.height * ratio);

            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;

            const ctx = canvas.getContext('2d');

            // fill white background (prevents black background on some JPEG conversions)
            ctx.fillStyle = '#fff';
            ctx.fillRect(0, 0, w, h);

            ctx.drawImage(img, 0, 0, w, h);

            const dataUrl = canvas.toDataURL('image/jpeg', quality);
            const base64 = (dataUrl.split(',')[1] || '').trim();
            resolve(base64);
          } catch (err) {
            reject(err);
          }
        };
        img.onerror = () => reject(new Error('Image load error'));
        img.src = ev.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const showOverlayWithTimer = ({ willRedirect }) => {
    setOverlayStatus('loading');
    setOverlayMessage('Submitting Membership Form');
    setOverlayVisible(true);

    if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);

    // Only show "redirecting" message if we really plan to redirect
    if (willRedirect) {
      overlayTimerRef.current = setTimeout(() => {
        setOverlayMessage('Redirecting to Payment checkout...');
        overlayTimerRef.current = null;
      }, 2500);
    }
  };

  const hideOverlay = (delayMs = 300) => {
    setTimeout(() => {
      setOverlayVisible(false);
      setOverlayMessage('');
      if (overlayTimerRef.current) {
        clearTimeout(overlayTimerRef.current);
        overlayTimerRef.current = null;
      }
    }, delayMs);
  };

  const showSuccessThenReset = async () => {
    if (overlayTimerRef.current) {
      clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }

    setOverlayStatus('success');
    setOverlayMessage('Form submitted successfully!');
    setOverlayVisible(true);

    await nextPaint();
    await sleep(2000);

    resetForm();
    hideOverlay(200);
  };

  const showErrorOverlay = async (msg = 'Submission failed. Please try again.') => {
    if (overlayTimerRef.current) {
      clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }

    setOverlayStatus('error');
    setOverlayMessage(msg);
    setOverlayVisible(true);

    await nextPaint();
    await sleep(2200);

    hideOverlay(200);
  };

// =====================================================
// MEMBERSHIP PRICING SOURCE + TALVIK PLANS (CI SAFE)
// =====================================================
useEffect(() => {
  const sn = storeInfoParsed?.storeNumber;
  if (!sn) return;

  let cancelled = false;

  const run = async () => {
    // reset per-store state
    setMembershipPlans([]);
    setPlansError('');
    setMembershipPricingSource('');
    setAllowKeyFobs(true);

    // --------------------------------------------
    // Fetch store config (Master Store List)
    // --------------------------------------------
    const fetchStoreConfig = async (storeNumber) => {
      if (!storeNumber) return null;

      if (!cancelled) {
        setStoreConfigLoading(true);
        setStoreConfigError('');
      }

      try {
        const requestBody = {
          target: 'Fetch_Master_Store_List',
          payload: {
            method: 'GET',
            mode: 'get',
            storeNumber: Number(storeNumber),
          },
        };

        console.log('🟩 [STORE CONFIG] Fetch request:', requestBody);

        const res = await fetch('https://rgs-a.azurewebsites.net/api/Master_Function', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(requestBody),
        });

        const raw = await res.text().catch(() => '');
        console.log('🟩 [STORE CONFIG] status:', res.status);
        // console.log('🟩 [STORE CONFIG] raw:', raw);

        if (!res.ok) throw new Error(raw || `HTTP ${res.status}`);

        let parsed = null;
        try {
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }

        // ✅ Your function returns: { data: { store: { ... } } }
        const storeObj = parsed?.data?.store ?? parsed?.store ?? null;

        if (!storeObj) {
          console.warn('🟨 [STORE CONFIG] No store object returned.');
          return { membershipPricingSource: '' };
        }

        // sometimes SharePoint-style payloads nest fields
        const f = storeObj?.fields ?? storeObj ?? {};

        const srcRaw =
          f?.membershipPricingSource ??
          f?.MembershipPricingSource ??
          storeObj?.membershipPricingSource ??
          storeObj?.MembershipPricingSource ??
          '';

        const src = String(srcRaw || '').trim();

        const allowKeyFobsRaw =
            f?.allowKeyFobs ??
            f?.AllowKeyFobs ??
            f?.KeyFobs ??
            storeObj?.allowKeyFobs ??
            storeObj?.AllowKeyFobs ??
            storeObj?.KeyFobs ??
            null;

          const allowKeyFobsParsed = parseBool(allowKeyFobsRaw, false);

        console.log('🟩 [STORE CONFIG] membershipPricingSource:', src);
        console.log('🟩 [STORE CONFIG] allowKeyFobs:', allowKeyFobsParsed);

        return { membershipPricingSource: src, allowKeyFobs: allowKeyFobsParsed };
      } catch (err) {
        console.warn('❌ [STORE CONFIG] failed:', err?.message || err);
        if (!cancelled) setStoreConfigError('Could not load store configuration.');
        return null;
      } finally {
        if (!cancelled) setStoreConfigLoading(false);
      }
    };

    // --------------------------------------------
    // Fetch Talvik membership plans (Membership_Plans)
    // --------------------------------------------
    const fetchMembershipPlans = async (storeNumber) => {
      if (!storeNumber) return;

      if (!cancelled) {
        setPlansLoading(true);
        setPlansError('');
      }

      try {
        const requestBody = {
          target: 'Membership_Plans',
          payload: {
            method: 'GET',
            mode: 'get',
            storeNumber: Number(storeNumber),
          },
        };

        console.log('🟦 [PLANS] Fetch request:', requestBody);

        const res = await fetch('https://rgs-a.azurewebsites.net/api/Master_Function', {
          method: 'POST', // Master_Function is always POST
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(requestBody),
        });

        const raw = await res.text().catch(() => '');
        console.log('🟦 [PLANS] status:', res.status);
        console.log('🟦 [PLANS] raw:', raw);

        if (!res.ok) throw new Error(raw || `HTTP ${res.status}`);

        let parsed = null;
        try {
          parsed = raw ? JSON.parse(raw) : null;
        } catch {
          parsed = null;
        }

        // Master wraps in { data: ... } for most targets
        const inner = parsed?.data ?? parsed ?? {};

        // Your Membership_Plans function returns:
        // { ok: true, count: X, items: [...] }
        const items = inner?.items ?? inner?.plans ?? inner?.value ?? inner ?? [];
        const list = Array.isArray(items) ? items : [];

        const normalized = list
          .map((row) => {
            const f = row?.fields ?? row ?? {};

            const planId = f?.planId != null ? String(f.planId) : '';
            const planName = String(f?.planName ?? f?.Title ?? '').trim();
            const durationMonths = Number(f?.durationMonths ?? 0);
            const signUpPrice = Number(f?.signUpPrice ?? 0);
            const renewalPrice =
              f?.renewalPrice == null || f?.renewalPrice === '' ? '' : Number(f.renewalPrice);
            const isActive = Boolean(f?.isActive);
            const isDefault = Boolean(f?.isDefault);
            const sortOrder = Number(f?.sortOrder ?? 9999);

            return {
              planId,
              planName,
              durationMonths,
              signUpPrice,
              renewalPrice,
              isActive,
              isDefault,
              sortOrder,
            };
          })
          .filter((p) => p.planId && p.planName && p.isActive)
          .sort((a, b) => {
            if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
            if (a.durationMonths !== b.durationMonths) return a.durationMonths - b.durationMonths;
            return a.planName.localeCompare(b.planName);
          });

        if (cancelled) return;

        setMembershipPlans(normalized);

        // Auto-select default plan if none chosen yet
        setFormData((prev) => {
          if (prev.planId) return prev;
          const defaultPlan = normalized.find((p) => p.isDefault) || normalized[0];
          if (!defaultPlan) return prev;

          const durationLabel = formatDurationLabel(defaultPlan.durationMonths);

          return {
            ...prev,
            planId: defaultPlan.planId,
            planName: defaultPlan.planName,
            durationMonths: String(defaultPlan.durationMonths),
            signUpPrice: String(defaultPlan.signUpPrice),
            renewalPrice: defaultPlan.renewalPrice === '' ? '' : String(defaultPlan.renewalPrice),
            // compatibility (legacy field)
            membershipDuration: durationLabel,
          };
        });
      } catch (err) {
        console.warn('❌ [PLANS] failed:', err?.message || err);
        if (!cancelled) {
          setMembershipPlans([]);
          setPlansError('Could not load membership plans.');
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    };

    // --------------------------------------------
    // Main flow
    // --------------------------------------------
    const cfg = await fetchStoreConfig(sn);

    const allowFobsFinal = parseBool(cfg?.allowKeyFobs, true);
      if (!cancelled) setAllowKeyFobs(allowFobsFinal);

    const src = String(cfg?.membershipPricingSource || '').trim();

    const normalizedSrc =
      src.toLowerCase() === 'talvik'
        ? 'Talvik'
        : src.toLowerCase() === 'clover'
        ? 'Clover'
        : '';

    const finalSrc = normalizedSrc || 'Clover';
    if (cancelled) return;

    setMembershipPricingSource(finalSrc);

    if (finalSrc === 'Talvik') {
      await fetchMembershipPlans(sn);
    }
  };

  run();

  return () => {
    cancelled = true;
  };
}, [storeInfoParsed?.storeNumber]);

    useEffect(() => {
        if (allowKeyFobs) return;

        setFormData((prev) => ({
          ...prev,
          accessType: 'Phone App',
          keyFobCode: '',
          addOn1Access: 'Phone App',
          addOn1FobCode: '',
          addOn2Access: 'Phone App',
          addOn2FobCode: '',
        }));
      }, [allowKeyFobs]);
      const handlePhoneChange = (e) => {
        let value = e.target.value.replace(/\D/g, '');


        value = value.substring(0, 10);

        if (value.length > 6) {
          value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
        } else if (value.length > 3) {
          value = `(${value.slice(0, 3)}) ${value.slice(3)}`;
        } else if (value.length > 0) {
          value = `(${value}`;
        }

        setFormData((prev) => ({
          ...prev,
          phone: value,
        }));
      };

  const handleChange = (e) => {
    const { name, value, type, files, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        smsConsentTimestamp: checked ? new Date().toISOString() : '',
      }));
      return;
    }

    // ✅ File selected: start compressing immediately so submit is faster
    if (type === 'file' && files && files.length > 0) {
      const file = files[0];
      setPhotoFile(file);
      setPhotoBase64('');
      setPhotoPrepError('');

      // don't rely on formData.photo (it can be mutated/serialized unexpectedly)
      setFormData((prev) => ({ ...prev, photo: null }));

      (async () => {
        try {
          const base64 = await resizeImageToBase64(file, 1200, 0.7);
          console.log('✅ Photo prepared. base64 length:', base64.length);
          setPhotoBase64(base64);
        } catch (err) {
          console.error('❌ Photo prep failed:', err);
          setPhotoPrepError('Photo processing failed. Please try a different photo.');
        }
      })();

      return;
    }

    // If user selects a membership plan dropdown
    if (name === 'planId') {
      const selected = membershipPlans.find((p) => p.planId === value);
      if (selected) {
        const durationLabel = formatDurationLabel(selected.durationMonths);

        setFormData((prev) => ({
          ...prev,
          planId: selected.planId,
          planName: selected.planName,
          durationMonths: String(selected.durationMonths),
          signUpPrice: String(selected.signUpPrice),
          renewalPrice: selected.renewalPrice === '' ? '' : String(selected.renewalPrice),
          // Keep old field populated for compatibility
          membershipDuration: durationLabel,
        }));
      } else {
        setFormData((prev) => ({ ...prev, planId: value }));
      }
      return;
    }

    if (!allowKeyFobs) {
      const isAccessField = name === 'accessType' || name === 'addOn1Access' || name === 'addOn2Access';
      if (isAccessField && String(value) === 'Key Fob') {
        return; // ignore
      }
      const isFobField = name === 'keyFobCode' || name === 'addOn1FobCode' || name === 'addOn2FobCode';
      if (isFobField) {
        return; // ignore fob code edits if disabled
      }
    }
      let newValue = value;

      if (name === 'phone') {
        newValue = value.replace(/\D/g, '').slice(0, 10);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: newValue,
      }));

    if (name === 'membershipLevel') {
      setShowAddOnsModal(value === 'Family');
    }
  };

  // Robust upload to Master_Function (Upload_ID_Save_ID)
  // ✅ Keeps your proven backend contract: payload.id + payload.fileContent
  const uploadPhotoToAzure = async (recordId) => {
    if (!recordId) {
      console.warn('uploadPhotoToAzure: missing recordId, skipping upload.');
      return '';
    }

    if (!photoFile) {
      console.log('uploadPhotoToAzure: no photoFile to upload.');
      return '';
    }

    // If photoBase64 not ready yet, wait briefly (up to 8s) while showing message
    if (!photoBase64) {
      console.log('uploadPhotoToAzure: photoBase64 not ready, waiting...');
      const start = Date.now();
      while (!photoBase64 && Date.now() - start < 8000) {
        await sleep(200);
      }
    }

    if (!photoBase64) {
      console.warn('uploadPhotoToAzure: photoBase64 still not available after waiting.');
      return '';
    }

    const uploadUrl = 'https://rgs-a.azurewebsites.net/api/Master_Function';

    const payload = {
      target: 'Upload_ID_Save_ID',
      payload: {
        id: recordId,
        storeNumber: formData.storeNumber,
        fileName: `photo-${Date.now()}.jpg`,
        fileContent: photoBase64,
      },
    };

    // Retry a couple times for transient mobile/cold-start issues
    const attempt = async (attemptNo) => {
      const res = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      });

      const raw = await res.text().catch(() => '');
      console.log(`uploadPhotoToAzure attempt ${attemptNo}:`, res.status, raw);

      if (!res.ok) {
        throw new Error(`Upload failed (${res.status}): ${raw}`);
      }

      // Tolerant parse
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        data = null;
      }

      return data?.url || '';
    };

    try {
      return await attempt(1);
    } catch (e1) {
      console.warn('uploadPhotoToAzure: attempt 1 failed:', e1?.message || e1);
      await sleep(800);
      try {
        return await attempt(2);
      } catch (e2) {
        console.warn('uploadPhotoToAzure: attempt 2 failed:', e2?.message || e2);
        await sleep(1600);
        try {
          return await attempt(3);
        } catch (e3) {
          console.error('uploadPhotoToAzure: attempt 3 failed:', e3?.message || e3);
          return '';
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.honeypot) {
      alert('Bot detected.');
      return;
    }

    const isValidStoreNumber = /^\d+$/.test(String(storeInfoParsed.storeNumber));
    if (!storeInfoParsed.storeName || !isValidStoreNumber) {
      alert('Invalid Link: Please scan the QR code again.');
      return;
    }

    // Require photo selection
    if (!photoFile) {
      alert('Please upload a photo ID before submitting.');
      return;
    }
    if (photoPrepError) {
      alert(photoPrepError);
      return;
    }

    if (!formData.smsConsent) {
      alert('Please consent to SMS notifications to continue.');
      return;
    }

    // ===== MEMBERSHIP PRICING SOURCE (MASTER STORE LIST) CHANGE START =====
    // If store says Talvik, user MUST select a plan and plans must exist.
    if (isPlanMode) {
      if (storeConfigLoading || plansLoading) {
        alert('Plans are still loading. Please wait a moment.');
        return;
      }
      if (!membershipPlans.length) {
        alert('This store is set to Talvik pricing, but no membership plans are configured yet.');
        return;
      }
      if (!formData.planId) {
        alert('Please select a membership plan.');
        return;
      }
    }

    const safeAccessType = allowKeyFobs ? formData.accessType : 'Phone App';

    let cleanPhone = formData.phone.replace(/\D/g, '');

    if (cleanPhone.length === 11 && cleanPhone.startsWith('1')) {
      cleanPhone = cleanPhone.substring(1);
    }

    setIsSubmitting(true);
    showOverlayWithTimer({ willRedirect: storeInfoParsed.isInstantAccess });

    try {
      const formRes = await fetch('https://rgs-a.azurewebsites.net/api/Master_Function', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          target: 'MasterSignUp',
          payload: {
            ...formData,
            phone: cleanPhone,
            storeName: storeInfoParsed.storeName,
            storeNumber: storeInfoParsed.storeNumber,
            accessMode: storeInfoParsed.accessMode,
            approval: 'Pending',

            // Pass pricing source to backend for clarity
            membershipPricingSource: membershipPricingSource || 'Clover',

            smsConsent: 'Subscribed',
            smsConsentTimestamp: formData.smsConsentTimestamp || null,

            allowKeyFobs: !!allowKeyFobs, // optional, but helpful for backend logging
            accessType: safeAccessType,
            keyFobCode: allowKeyFobs ? formData.keyFobCode : '',

            // If Talvik pricing, include membershipPlan object
            membershipPlan: isPlanMode
              ? {
                  planId: formData.planId,
                  planName: formData.planName,
                  durationMonths: Number(formData.durationMonths || 0),
                  signUpPrice: Number(formData.signUpPrice || 0),
                  renewalPrice:
                    formData.renewalPrice === '' ? null : Number(formData.renewalPrice || 0),
                }
              : null,

            addOns: [
              {
                fullName: formData.addOn1Name,
                email: formData.addOn1Email,
                phoneNumber: formData.addOn1Phone,
                accessType: allowKeyFobs ? formData.addOn1Access : 'Phone App',
                keyFobCode: allowKeyFobs ? formData.addOn1FobCode : '',
              },
              {
                fullName: formData.addOn2Name,
                email: formData.addOn2Email,
                phoneNumber: formData.addOn2Phone,
                accessType: allowKeyFobs ? formData.addOn2Access : 'Phone App',
                keyFobCode: allowKeyFobs ? formData.addOn2FobCode : '',
              },
            ],
          },
        }),
      });

      const text = await formRes.text();
      let responseData = null;
      try {
        responseData = text ? JSON.parse(text) : null;
      } catch {
        responseData = null;
      }

      if (!formRes.ok) {
        const msg =
          responseData && responseData.message
            ? responseData.message
            : `Form submission failed (${formRes.status})`;
        throw new Error(msg);
      }

      const createdId = responseData?.id;
      const checkoutUrl = responseData?.checkoutUrl;

      console.log('MasterSignUp response:', responseData);

      if (!createdId) {
        alert('Error: ID not returned from SharePoint.');
        hideOverlay();
        setIsSubmitting(false);
        return;
      }

      // ✅ Upload photo BEFORE redirect in IA so manager always has it
      setOverlayMessage('Uploading photo...');
      const uploadedUrl = await uploadPhotoToAzure(createdId);
      if (!uploadedUrl) {
        console.warn('Photo upload returned empty url. Proceeding, but check server logs if needed.');
      } else {
        console.log('Photo uploaded to:', uploadedUrl);
      }

      // IA flow -> redirect after upload
      if (checkoutUrl) {
        if (overlayTimerRef.current) {
          clearTimeout(overlayTimerRef.current);
          overlayTimerRef.current = null;
        }
        setOverlayStatus('loading');
        setOverlayMessage('Redirecting to Payment checkout...');
        setOverlayVisible(true);

        // Ensure overlay paints on mobile
        await nextPaint();
        await sleep(450);

        try {
          if (window.top && window.top !== window) {
            window.top.location.href = checkoutUrl;
          } else {
            window.location.href = checkoutUrl;
          }
        } catch (err) {
          window.location.assign(checkoutUrl);
        }
        return;
      }

      await showSuccessThenReset();
      // =========================================================
      // ✅✅✅ SUCCESS UI OVERLAY CHANGE END
      // =========================================================
    } catch (err) {
      console.error(err);

      // =========================================================
      // ✅✅✅ SUCCESS UI OVERLAY CHANGE START
      // Show ❌ in overlay instead of hard alert
      // =========================================================
      await showErrorOverlay('Submission failed. Please try again.');
      // =========================================================
      // ✅✅✅ SUCCESS UI OVERLAY CHANGE END
      // =========================================================
    } finally {
      setIsSubmitting(false);
    }
  };

  // Inline styles for overlay + spinner (kept inline so it works on mobile regardless of CSS)
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    color: '#fff',
    flexDirection: 'column',
    padding: 20,
    textAlign: 'center',
  };

  const spinnerStyle = {
    width: 64,
    height: 64,
    border: '8px solid rgba(255,255,255,0.2)',
    borderTop: '8px solid #fff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: 20,
  };

  const messageStyle = { fontSize: 18, fontWeight: 600, marginBottom: 8 };
  const subMessageStyle = { fontSize: 14, opacity: 0.95 };

  // ✅ IMPORTANT: early return must be AFTER all hooks
  if (!isValid) {
    return <Navigate to="/" replace />;
  }

  return (
  <>
    <form className="container" onSubmit={handleSubmit}>
      <h1 className="header">Membership Sign Up</h1>

      <div className="store-name">{formatStoreName(storeInfoParsed.storeName)}</div>

      {storeInfoParsed.isInstantAccess && (
        <div
          style={{
            textAlign: 'center',
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'rgba(39, 174, 96, 0.12)',
            fontWeight: 700,
          }}
        >
          Instant Access (payment required)
        </div>
      )}

      <div className="cardSection">
        <div className="card">
          <input
            className="input"
            name="firstName"
            placeholder="First Name"
            value={formData.firstName}
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="lastName"
            placeholder="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="address"
            placeholder="Mailing Address"
            value={formData.address}
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="state"
            placeholder="State"
            value={formData.state}
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="zip"
            placeholder="Zip"
            value={formData.zip}
            onChange={handleChange}
            required
          />
          <input
            className="input"
            name="phone"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={handlePhoneChange}
            required
            inputMode="tel"
            autoComplete="tel"
          />

          {/* =========================================================
              ✅✅✅ SMS CONSENT ADDITION START (visible consent + checkbox)
              This is what carriers review. Keep it visible near phone field.
             ========================================================= */}
          <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.35, opacity: 0.9 }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="smsConsent"
                checked={!!formData.smsConsent}
                onChange={handleChange}
                style={{ marginTop: 3 }}
                required
              />
              <span>
                I agree to receive text messages from Talvik about my membership (renewal reminders,
                payment confirmations, and access notifications). Message frequency varies. Message
                &amp; data rates may apply. Reply <strong>STOP</strong> to opt out.
              </span>
            </label>
          </div>

          <input
            className="input"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleChange}
            required
          />

          <label className="label">Access Type</label>

          {allowKeyFobs ? (
            <>
              <select
                className="input"
                name="accessType"
                value={formData.accessType}
                onChange={handleChange}
                required
              >
                <option value="">Select Access Type</option>
                <option value="Phone App">Phone App</option>
                <option value="Key Fob">Key Fob</option>
              </select>

              {formData.accessType === 'Key Fob' && (
                <input
                  className="input"
                  name="keyFobCode"
                  placeholder="Fob Code"
                  value={formData.keyFobCode}
                  onChange={handleChange}
                />
              )}
            </>
          ) : (
            <div
              style={{
                marginTop: 6,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.06)',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              Phone App Only
            </div>
          )}

          {/* ===== MEMBERSHIP PRICING SOURCE (MASTER STORE LIST) CHANGE START ===== */}
          {(storeConfigLoading || storeConfigError) && (
            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
              {storeConfigLoading ? 'Loading store configuration…' : storeConfigError}
            </div>
          )}

          {/* ===== TALVIK PLANS UI CHANGE START ===== */}
          {isPlanMode ? (
            <>
              {plansLoading ? (
                <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
                  Loading membership plans…
                </div>
              ) : null}

              {plansError ? (
                <div style={{ marginTop: 10, fontSize: 13, color: '#ffdddd' }}>{plansError}</div>
              ) : null}

              {!plansLoading && membershipPlans.length === 0 ? (
                <div style={{ marginTop: 10, fontSize: 13, color: '#ffdddd' }}>
                  This store is set to Talvik pricing, but no membership plans exist yet.
                </div>
              ) : null}

              <label className="label">Membership Plan</label>
              <select
                className="input"
                name="planId"
                value={formData.planId}
                onChange={handleChange}
                required
                disabled={plansLoading || membershipPlans.length === 0}
              >
                <option value="">Select Plan</option>
                {membershipPlans.map((p) => (
                  <option key={p.planId} value={p.planId}>
                    {p.planName} ({p.durationMonths} mo) - ${Number(p.signUpPrice).toFixed(2)}
                  </option>
                ))}
              </select>
            </>
          ) : (
            <>
              <label className="label">Membership Duration</label>
              <select
                className="input"
                name="membershipDuration"
                value={formData.membershipDuration}
                onChange={handleChange}
                required
              >
                <option value="">Select Duration</option>
                <option value="3 Month">3 Month</option>
                <option value="6 Month">6 Month</option>
                <option value="1 Year">1 Year</option>
              </select>
            </>
          )}
          {/* ===== TALVIK PLANS UI CHANGE END ===== */}

          <label className="label">Membership Level</label>
          <select
            className="input"
            name="membershipLevel"
            value={formData.membershipLevel}
            onChange={handleChange}
            required
          >
            <option value="">Select Level</option>
            <option value="Single">Single</option>
            <option value="Family">Family</option>
          </select>

          <label className="label">Upload Photo ID</label>
          <input
            ref={photoInputRef}
            className="input"
            type="file"
            name="photo"
            accept="image/*"
            capture="environment"
            onChange={handleChange}
            required
          />

          {/* Optional UX hints */}
          {photoFile && !photoBase64 && !photoPrepError && (
            <div style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>Processing photo…</div>
          )}
          {photoPrepError && (
            <div style={{ marginTop: 10, fontSize: 13, color: '#ffdddd' }}>{photoPrepError}</div>
          )}
        </div>
      </div>

      <input
        className="honeypot"
        name="honeypot"
        value={formData.honeypot}
        onChange={handleChange}
        autoComplete="off"
        tabIndex="-1"
      />

      <button className="submitButton" type="submit" disabled={isSubmitting}>
        {isSubmitting ? 'Submitting…' : 'Submit'}
      </button>

      <div
        style={{
          textAlign: 'center',
          marginTop: 18,
          fontSize: 12,
          opacity: 0.75,
          lineHeight: 1.4,
        }}
      >
        <strong>Talvik</strong> is powered by <strong>Rural Grocery Solutions</strong>
        <br />
        Supporting rural grocery stores & communities
      </div>
    </form>

    {/* ✅✅✅ FIXED: Add-Ons Modal (properly closed tags + Key Fob conditional) */}
    {showAddOnsModal && (
      <div className="modalOverlay">
        <div className="modalContent">
          <h2 className="header">Add-On Members</h2>

          <div className="addonTitle">First Add-On</div>
          <div className="addonCard">
            <div className="addonField">
              Name:{' '}
              <input
                name="addOn1Name"
                value={formData.addOn1Name}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="addonField">
              Email:{' '}
              <input
                name="addOn1Email"
                value={formData.addOn1Email}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="addonField">
              Phone:{' '}
              <input
                name="addOn1Phone"
                value={formData.addOn1Phone}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="addonField">
              Access Type:
              <select
                name="addOn1Access"
                value={formData.addOn1Access}
                onChange={handleChange}
                className="input"
              >
                <option value="Phone App">Phone App</option>
                {allowKeyFobs && <option value="Key Fob">Key Fob</option>}
              </select>
            </div>

            {allowKeyFobs && formData.addOn1Access === 'Key Fob' && (
              <div className="addonField">
                Fob Code:{' '}
                <input
                  name="addOn1FobCode"
                  value={formData.addOn1FobCode}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            )}
          </div>

          <div className="addonTitle">Second Add-On</div>
          <div className="addonCard">
            <div className="addonField">
              Name:{' '}
              <input
                name="addOn2Name"
                value={formData.addOn2Name}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="addonField">
              Email:{' '}
              <input
                name="addOn2Email"
                value={formData.addOn2Email}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="addonField">
              Phone:{' '}
              <input
                name="addOn2Phone"
                value={formData.addOn2Phone}
                onChange={handleChange}
                className="input"
              />
            </div>

            <div className="addonField">
              Access Type:
              <select
                name="addOn2Access"
                value={formData.addOn2Access}
                onChange={handleChange}
                className="input"
              >
                <option value="Phone App">Phone App</option>
                {allowKeyFobs && <option value="Key Fob">Key Fob</option>}
              </select>
            </div>

            {allowKeyFobs && formData.addOn2Access === 'Key Fob' && (
              <div className="addonField">
                Fob Code:{' '}
                <input
                  name="addOn2FobCode"
                  value={formData.addOn2FobCode}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            )}
          </div>

          <div className="modalActions">
            <button
              type="button"
              className="submitButton"
              onClick={() => setShowAddOnsModal(false)}
            >
              Save Add-Ons
            </button>
            <button
              type="button"
              className="secondaryButton"
              onClick={() => setShowAddOnsModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}

    {overlayVisible && (
      <div style={overlayStyle} aria-live="assertive">
        {overlayStatus === 'loading' ? (
          <div style={spinnerStyle} />
        ) : (
          <div
            style={{
              fontSize: 64,
              lineHeight: 1,
              marginBottom: 14,
              transform: 'translateY(-2px)',
            }}
          >
            {overlayStatus === 'success' ? '✅' : '❌'}
          </div>
        )}

        <div style={messageStyle}>{overlayMessage}</div>

        {overlayStatus === 'loading' ? (
          <div style={subMessageStyle}>Please do not close this window.</div>
        ) : (
          <div style={subMessageStyle}>
            {overlayStatus === 'success' ? 'Returning to form…' : 'Please try again.'}
          </div>
        )}
      </div>
    )}

    <style>{`
      @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    `}</style>
  </>
);
}